import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Animated,
  Platform, Image,
} from 'react-native';
import AppModal from './AppModal';
import { showAppAlert } from './AppDialog';
import { colors, spacing, radius, shadow } from '../theme';
import { PREMIUM_PLANS, PREMIUM_PLANS_YEARLY, PREMIUM_FEATURES, FREE_LIMITS, TIERS } from '../services/PremiumService';
import { useAuth } from '../context/AuthContext';
import PlanComparisonTable from './PlanComparisonTable';
import { startCheckout, parsePlanId } from '../services/StripeService';
import { openLegalUrl } from '../utils/openLegalUrl';
import { getCurrentLang } from '../i18n';

const { width: W } = Dimensions.get('window');

export default function PremiumModal({ visible, onClose, onSubscribe, trigger, currentTier = 'free', currentPeriod: currentPeriodProp = null }) {
  // v1.12.634 — Stabilisation du onClose via ref pour éviter que AppModal
  // useEffect [visible, close] cleanup → history.back() → ferme la modal au
  // moindre re-render parent (scroll, focus, etc.). 4ème occurrence du pattern,
  // voir mémoire [[appmodal-callback-stable]].
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const stableOnClose = useCallback(() => { onCloseRef.current?.(); }, []);
  // v1.12.592 — Toggle mensuel/annuel + selected = tier (basic/premium/gold)
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [selected, setSelected] = useState('premium');
  const [busy, setBusy] = useState(false);
  // v1.12.598 — Période actuelle de l'abo, plusieurs sources avec fallback :
  //   1. currentPeriodProp (passé en prop par le parent)
  //   2. user.subscriptionPeriod (AuthContext, déjà chargé par /profile)
  //   3. fetch /subscription (fallback)
  const { user, getValidToken } = useAuth() || {};
  const [currentPeriod, setCurrentPeriod] = useState(currentPeriodProp || user?.subscriptionPeriod || null);
  useEffect(() => {
    if (!visible) return;
    if (currentTier === 'free') {
      setCurrentPeriod(null);
      return;
    }
    // 1. prop explicite
    if (currentPeriodProp) {
      setCurrentPeriod(currentPeriodProp);
      setBillingPeriod(currentPeriodProp);
      return;
    }
    // 2. user context (le plus rapide, déjà chargé via /profile)
    if (user?.subscriptionPeriod) {
      const period = user.subscriptionPeriod === 'yearly' ? 'yearly' : 'monthly';
      setCurrentPeriod(period);
      setBillingPeriod(period);
      return;
    }
    // 3. fetch /subscription en dernier recours
    setCurrentPeriod(null);
    (async () => {
      try {
        const { fetchSubscription } = require('../services/SubscriptionService');
        const token = getValidToken ? await getValidToken() : null;
        const sub = await fetchSubscription(user?.id || user?.sub, token);
        console.log('[PremiumModal] fetched subscription:', sub?.status, sub?.period);
        if (sub?.status === 'active' && sub.period) {
          const period = sub.period === 'yearly' ? 'yearly' : 'monthly';
          setCurrentPeriod(period);
          setBillingPeriod(period);
        }
      } catch (e) {
        console.warn('[PremiumModal] fetchSubscription failed:', e?.message);
      }
    })();
  }, [visible, currentTier, currentPeriodProp, user?.subscriptionPeriod, user?.id]);

  // Selon le toggle, on lit la bonne liste de plans (mensuel ou annuel équivalent mensuel)
  const plans = billingPeriod === 'yearly' ? PREMIUM_PLANS_YEARLY : PREMIUM_PLANS;
  // ID complet du plan sélectionné (ex: 'premium' ou 'premium_yearly')
  const selectedPlanId = billingPeriod === 'yearly' ? `${selected}_yearly` : selected;
  const plan = plans.find(p => p.tier === selected) || plans[0];
  // % de réduction annuel basé sur Premium (référence)
  const yearlyDiscountPct = TIERS.premium?.priceMonthly && TIERS.premium?.priceYearlyMonth
    ? Math.round((1 - TIERS.premium.priceYearlyMonth / TIERS.premium.priceMonthly) * 100)
    : 17;

  // v1.12.369 — GA4 event: premium_modal_open (émis 1x par session d'ouverture)
  const wasVisibleRef = useRef(false);
  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        trackEvent('premium_modal_open', { trigger: trigger || 'unknown' });
      } catch {}
    }
    wasVisibleRef.current = visible;
  }, [visible, trigger]);

  // v1.12.592 — handlePlanSelect prend tierId ('basic'|'premium'|'gold')
  const handlePlanSelect = (tierId) => {
    setSelected(tierId);
    try {
      const { trackEvent } = require('../services/AnalyticsService');
      trackEvent('premium_plan_selected', { plan_id: tierId, billing_period: billingPeriod, trigger: trigger || 'unknown' });
    } catch {}
  };

  // Sur web : on intercepte le clic pour rediriger vers Stripe Checkout au
  // lieu d'appeler le handler IAP du parent (qui passerait par RevenueCat,
  // indisponible sur web). Sur mobile : comportement inchangé.
  const handleSubscribePress = async () => {
    if (Platform.OS === 'web') {
      try {
        setBusy(true);
        // v1.12.658 — Si user déjà abonné: change-subscription
        const hasActiveSub = currentTier && currentTier !== 'free';
        if (hasActiveSub) {
          // v1.12.661/662 — Confirmation avec message spécifique selon le cas
          setBusy(false);
          const tierLabel = selected.charAt(0).toUpperCase() + selected.slice(1);
          const periodLabel = billingPeriod === 'yearly' ? 'annuel' : 'mensuel';
          const currentLabel = (currentTier || 'free').charAt(0).toUpperCase() + (currentTier || 'free').slice(1);
          const isMonthlyToYearlySame = currentTier === selected && billingPeriod === 'yearly';
          const isYearlyToMonthlySame = currentTier === selected && billingPeriod === 'monthly';
          const YEARLY_PRICES = { basic: 59.90, premium: 99.90, gold: 169.90 };
          let confirmMsg;
          if (isMonthlyToYearlySame && YEARLY_PRICES[selected]) {
            confirmMsg = `Passer ${tierLabel} en annuel :\n\n• Vous payez ${YEARLY_PRICES[selected]} € maintenant pour 1 an.\n• Vos jours restants de l'abonnement mensuel sont préservés.\n• L'annuel commencera à la fin de votre période mensuelle.\n• Aucun jour perdu.`;
          } else if (isYearlyToMonthlySame) {
            confirmMsg = `Passer ${tierLabel} en mensuel :\n\n• Vous gardez votre abonnement annuel jusqu'à expiration.\n• Aucun remboursement.\n• Bascule automatique en mensuel à la fin de l'année.\n• Aucun prélèvement immédiat.`;
          } else {
            confirmMsg = `Passer de ${currentLabel} à ${tierLabel} ${periodLabel} ?\n\nProrata facturé si upgrade, ou bascule fin de période si downgrade.`;
          }
          const confirmIdx = await showAppAlert('Confirmer le changement', confirmMsg, [
            { text: 'Annuler', style: 'cancel' },
            { text: 'Confirmer', style: 'default' },
          ]);
          if (confirmIdx !== 1) return;
          setBusy(true);
          const { changeSubscription } = require('../services/StripeService');
          const { showProcessing, hideProcessing } = require('./ProcessingOverlay');
          try {
            showProcessing('Paiement en cours…', `Finalisation de votre abonnement ${selected} ${billingPeriod === 'yearly' ? 'annuel' : 'mensuel'}.`);
            const result = await changeSubscription(selected, billingPeriod);
            hideProcessing();
            setBusy(false);
            if (result?.mode === 'updated') {
              showAppAlert(
                'Abonnement modifié',
                `Votre nouveau plan ${selected} ${billingPeriod === 'yearly' ? 'annuel' : 'mensuel'} est actif immédiatement.`
              );
            } else if (result?.mode === 'prepaid_yearly') {
              const effectiveDate = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString('fr-FR') : '';
              const renewDate = result.nextRenewalAt ? new Date(result.nextRenewalAt).toLocaleDateString('fr-FR') : '';
              showAppAlert(
                '✓ Abonnement annuel confirmé',
                `Paiement de ${result.amountPaid} € reçu.\n\nVotre abonnement annuel commencera le ${effectiveDate}.\nProchain renouvellement : ${renewDate}.`
              );
            } else if (result?.mode === 'scheduled') {
              const date = result.effectiveAt ? new Date(result.effectiveAt).toLocaleDateString('fr-FR') : '';
              showAppAlert(
                'Changement programmé',
                `Votre plan ${selected} ${billingPeriod === 'yearly' ? 'annuel' : 'mensuel'} sera actif le ${date}.`
              );
            }
            stableOnClose();
            return;
          } catch (e) {
            hideProcessing();
            const code = e?.code || '';
            const msg = e?.message || '';
            if (code === 'no_active_subscription' || msg.includes('no_active_subscription') || msg.includes('Use create-checkout-session') || msg.includes('does not exist')) {
              console.warn('[PremiumModal] changeSubscription failed → fallback Checkout:', msg);
              const { purchaseSubscriptionWithCredits } = require('../services/CreditAwarePurchase');
              await purchaseSubscriptionWithCredits({ tier: selected, period: billingPeriod, user, getValidToken });
              return;
            }
            if (code === 'already_on_this_plan' || msg.includes('already_on_this_plan')) {
              showAppAlert(
                'Plan identique',
                `Vous êtes déjà abonné à ${tierLabel} ${periodLabel}.`
              );
              return;
            }
            if (code === 'already_scheduled_to_this_plan' || msg.includes('already_scheduled_to_this_plan')) {
              showAppAlert(
                'Changement déjà programmé',
                `Un passage à ${tierLabel} ${periodLabel} est déjà programmé.`
              );
              return;
            }
            // v1.12.670 — Conflit pending
            if (code === 'pending_change_conflict' || msg.includes('pending_change_conflict')) {
              showAppAlert(
                'Changement en attente',
                'Vous avez déjà un changement programmé. Allez dans Profil pour l\'annuler avant d\'en faire un nouveau.'
              );
              return;
            }
            throw e;
          }
        }
        // Premier achat depuis free: Checkout (avec application Crédit Mytenue auto)
        try {
          const { purchaseSubscriptionWithCredits } = require('../services/CreditAwarePurchase');
          await purchaseSubscriptionWithCredits({ tier: selected, period: billingPeriod, user, getValidToken });
        } catch (e) {
          // v1.12.672 — Fallback auto vers change-sub si user déjà abonné
          const code = e?.code || '';
          if (code === 'already_subscribed' || (e?.message || '').includes('already_subscribed')) {
            const { changeSubscription } = require('../services/StripeService');
            const result = await changeSubscription(selected, billingPeriod);
            setBusy(false);
            if (result?.mode === 'updated') {
              showAppAlert('Abonnement modifié', `Plan ${selected} ${billingPeriod} actif immédiatement.`);
            } else if (result?.mode === 'scheduled') {
              showAppAlert('Changement programmé', 'Bascule à la fin de la période.');
            } else if (result?.mode === 'prepaid_yearly') {
              showAppAlert('✓ Annuel confirmé', `Paiement de ${result.amountPaid}€ reçu.`);
            }
            stableOnClose();
            return;
          }
          throw e;
        }
      } catch (e) {
        setBusy(false);
        showAppAlert('Paiement indisponible', e?.message || 'Erreur Stripe');
      }
      return;
    }
    // v1.12.945 — Mobile : confirmation avant un switch de période (mensuel ↔ annuel).
    if (selected === currentTier && currentPeriod && billingPeriod !== currentPeriod) {
      const _isFR = (getCurrentLang() || 'fr').toLowerCase().startsWith('fr');
      const toYearly = billingPeriod === 'yearly';
      const idx = await showAppAlert(
        _isFR ? 'Confirmer le changement' : 'Confirm change',
        _isFR
          ? (toYearly
              ? "Passer en formule annuelle ?\n\nLe changement s'applique via l'App Store : immédiatement (avec ajustement de prix au prorata) si c'est une montée en gamme, sinon au prochain renouvellement."
              : "Passer en formule mensuelle ?\n\nLe changement prend effet au prochain renouvellement de votre abonnement. Aucun prélèvement immédiat.")
          : (toYearly
              ? "Switch to the yearly plan?\n\nThe change is applied via the App Store: immediately (with a prorated price adjustment) if it's an upgrade, otherwise at your next renewal."
              : "Switch to the monthly plan?\n\nThe change takes effect at your next renewal. No immediate charge."),
        [
          { text: _isFR ? 'Annuler' : 'Cancel', style: 'cancel' },
          { text: _isFR ? 'Confirmer' : 'Confirm' },
        ],
      );
      if (idx !== 1) return;
    }
    onSubscribe && onSubscribe(selectedPlanId);
  };

  // v1.12.594 — Messages basés sur les limites du tier ACTUEL de l'user
  // (pas FREE_LIMITS hardcodé). Ex : pour Basic, "vos 20 analyses IA" pas "5".
  // v1.12.937 — Langue du profil (l'écran était figé en FR).
  const isFR = (getCurrentLang() || 'fr').toLowerCase().startsWith('fr');
  const currentTierLimits = (TIERS[currentTier] || TIERS.free);
  const currentTierLabel = currentTier === 'free'
    ? (isFR ? 'Gratuit' : 'Free')
    : (currentTierLimits.label || (currentTier ? currentTier[0].toUpperCase() + currentTier.slice(1) : 'Free'));
  const triggerMsg = (isFR ? {
    wardrobe: `Vous avez atteint la limite de ${currentTierLimits.wardrobe_items} pièces dans votre dressing ${currentTierLabel}.`,
    ai:       `Vous avez utilisé vos ${currentTierLimits.ai_requests} analyses IA quotidiennes (${currentTierLabel}).`,
    looks:    `Vous avez généré le maximum de ${currentTierLimits.looks_per_day === Infinity ? '∞' : currentTierLimits.looks_per_day} tenues aujourd'hui (${currentTierLabel}).`,
    tryon:    `Vous avez utilisé vos ${currentTierLimits.tryons_per_month} essais virtuels du mois (${currentTierLabel}).`,
  } : {
    wardrobe: `You've reached the limit of ${currentTierLimits.wardrobe_items} items in your ${currentTierLabel} wardrobe.`,
    ai:       `You've used your ${currentTierLimits.ai_requests} daily AI analyses (${currentTierLabel}).`,
    looks:    `You've generated the maximum of ${currentTierLimits.looks_per_day === Infinity ? '∞' : currentTierLimits.looks_per_day} outfits today (${currentTierLabel}).`,
    tryon:    `You've used your ${currentTierLimits.tryons_per_month} virtual try-ons this month (${currentTierLabel}).`,
  })[trigger] || (isFR ? `Passez à un plan supérieur pour débloquer plus de fonctionnalités.` : `Upgrade to a higher plan to unlock more features.`);

  // v1.12.937 — Features bilingues, SANS Voyages (descopé), Cashback → Crédits MyTenue.
  const FEATURES = isFR ? [
    { icon: '🧥', label: 'Dressing étendu',              sub: `${TIERS.free.wardrobe_items} → ${TIERS.gold.wardrobe_items} pièces selon l'abonnement` },
    { icon: '🤖', label: "Plus d'analyses IA",            sub: `${TIERS.free.ai_requests} → ${TIERS.gold.ai_requests} analyses par jour` },
    { icon: '✨', label: 'Looks illimités',               sub: "Dès l'abonnement Premium" },
    { icon: '🪞', label: 'Essais virtuels sur mannequin', sub: `${TIERS.basic.tryons_per_month}/m (Basic) · ${TIERS.premium.tryons_per_month}/m (Premium) · ${TIERS.gold.tryons_per_month}/m (Gold)` },
    { icon: '🛍️', label: 'Suggestions exclusives',        sub: 'Articles sélectionnés par notre IA' },
    { icon: '⭐', label: 'Crédits MyTenue préservés',     sub: `Jusqu'à ${TIERS.gold.cashback?.displayMaxEurPer100?.toFixed(2).replace('.', ',') || '4,80'} € de crédits pour 100 € d'achat (Gold)` },
  ] : [
    { icon: '🧥', label: 'Extended wardrobe',             sub: `${TIERS.free.wardrobe_items} → ${TIERS.gold.wardrobe_items} items depending on plan` },
    { icon: '🤖', label: 'More AI analyses',              sub: `${TIERS.free.ai_requests} → ${TIERS.gold.ai_requests} analyses per day` },
    { icon: '✨', label: 'Unlimited looks',               sub: 'From the Premium plan' },
    { icon: '🪞', label: 'Virtual try-on on models',      sub: `${TIERS.basic.tryons_per_month}/mo (Basic) · ${TIERS.premium.tryons_per_month}/mo (Premium) · ${TIERS.gold.tryons_per_month}/mo (Gold)` },
    { icon: '🛍️', label: 'Exclusive suggestions',         sub: 'Items hand-picked by our AI' },
    { icon: '⭐', label: 'MyTenue Credits preserved',     sub: `Up to €${TIERS.gold.cashback?.displayMaxEurPer100?.toFixed(2) || '4.80'} in credits per €100 spent (Gold)` },
  ];

  // v1.12.654 — Render block retiré: le block est sur les auto-triggers
  // dans usePremiumGuard. Si on arrive ici visible=true, c'est manuel/légitime.

  return (
    <AppModal visible={visible} transparent animationType="slide"
      onRequestClose={stableOnClose} statusBarTranslucent>
      <View style={s.overlay}>
        {/* v1.12.634 — TouchableOpacity backdrop retiré : sur web, un scroll
             rapide peut être interprété comme onPress, fermant la modal par
             erreur. AppModal gère déjà le backdrop selon webBackdropClosable. */}

        <View style={s.sheet}>
          {/* Handle */}
          <View style={s.handle} />

          {/* v1.12.937 — Bouton fermer en haut à droite */}
          <TouchableOpacity onPress={stableOnClose} style={s.closeBtn} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Text style={s.closeBtnTxt}>✕</Text>
          </TouchableOpacity>

          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 40 }}>

            {/* Header — v1.12.937 : logo MyTenue à la place de la couronne, titre "Mytenue Premium" */}
            <View style={s.header}>
              <Image source={require('../../assets/logo-login-gold.png')} style={s.logo} resizeMode="contain" />
              <Text style={s.title}>Mytenue Premium</Text>
              <Text style={s.subtitle}>{triggerMsg}</Text>
            </View>

            {/* Features */}
            <View style={s.features}>
              {FEATURES.map((f, i) => (
                <View key={i} style={s.featureRow}>
                  <Text style={s.featureIcon}>{f.icon}</Text>
                  <View style={s.featureText}>
                    <Text style={s.featureLabel}>{f.label}</Text>
                    <Text style={s.featureSub}>{f.sub}</Text>
                  </View>
                  <Text style={s.featureCheck}>✓</Text>
                </View>
              ))}
            </View>

            {/* ═══ Tableau comparatif des plans ═══ */}
            <View style={{ paddingHorizontal: spacing.md, marginBottom: spacing.md }}>
              <PlanComparisonTable
                currentTier={currentTier}
                showHeader={false}
                compact
              />
            </View>

            {/* v1.12.592 — Toggle Mensuel / Annuel */}
            <View style={{
              flexDirection: 'row', alignSelf: 'center',
              marginBottom: 16, marginHorizontal: spacing.xl,
              backgroundColor: '#F0F0F0', borderRadius: 12, padding: 3,
            }}>
              <TouchableOpacity
                onPress={() => setBillingPeriod('monthly')}
                style={{
                  paddingVertical: 8, paddingHorizontal: 18, borderRadius: 10,
                  backgroundColor: billingPeriod === 'monthly' ? '#FFF' : 'transparent',
                  ...(billingPeriod === 'monthly' ? shadow.sm : {}),
                }}
              >
                <Text style={{ fontSize: 13, fontWeight: '600', color: billingPeriod === 'monthly' ? '#111' : '#888' }}>
                  {isFR ? 'Mensuel' : 'Monthly'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setBillingPeriod('yearly')}
                style={{
                  paddingVertical: 8, paddingHorizontal: 18, borderRadius: 10,
                  backgroundColor: billingPeriod === 'yearly' ? '#FFF' : 'transparent',
                  flexDirection: 'row', alignItems: 'center', gap: 6,
                  ...(billingPeriod === 'yearly' ? shadow.sm : {}),
                }}
              >
                <Text style={{ fontSize: 13, fontWeight: '600', color: billingPeriod === 'yearly' ? '#111' : '#888' }}>
                  {isFR ? 'Annuel' : 'Yearly'}
                </Text>
                <View style={{ backgroundColor: '#D4AF37', paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4 }}>
                  <Text style={{ fontSize: 10, fontWeight: '700', color: '#FFF' }}>
                    {isFR ? '2 mois offerts' : '2 months free'}
                  </Text>
                </View>
              </TouchableOpacity>
            </View>

            {/* Plans */}
            <View style={s.plans}>
              {plans.map(p => {
                // v1.12.597 — badge "ACTUEL" : tier ET période doivent matcher EXACTEMENT.
                // Si currentPeriod pas encore chargé (loading) → pas de badge (évite faux positif).
                const isCurrent = p.tier === currentTier
                  && currentPeriod
                  && billingPeriod === currentPeriod;
                return (
                <TouchableOpacity
                  key={p.id}
                  style={[s.planCard, selected === p.tier && s.planCardActive, isCurrent && { borderColor: '#2E7D32', borderWidth: 2 }]}
                  onPress={() => handlePlanSelect(p.tier)}
                  activeOpacity={0.8}>
                  {isCurrent ? (
                    <View style={[s.planBadge, { backgroundColor: '#2E7D32' }]}>
                      <Text style={s.planBadgeTxt}>{isFR ? 'ACTUEL' : 'CURRENT'}</Text>
                    </View>
                  ) : p.badge && (
                    <View style={s.planBadge}>
                      <Text style={s.planBadgeTxt}>{isFR ? p.badge : (p.badge === 'Populaire' ? 'Popular' : p.badge === 'Sans limite' ? 'Unlimited' : p.badge)}</Text>
                    </View>
                  )}
                  {p.popular && <View style={s.popularDot} />}
                  <Text style={[s.planLabel, selected === p.tier && s.planLabelActive]}>
                    {p.label}
                  </Text>
                  {/* Apple 3.1.2(c) : le MONTANT FACTURÉ est l'élément le plus visible.
                      En annuel → total annuel en gros (59,90 €/an), mensuel calculé en petit. */}
                  {billingPeriod === 'yearly' && p.yearly ? (
                    <>
                      <View style={s.planPriceRow}>
                        <Text style={[s.planPrice, selected === p.tier && s.planPriceActive]}>
                          {Number(p.yearly).toFixed(2).replace('.', ',')} €
                        </Text>
                        <Text style={s.planPeriod}>/{isFR ? 'an' : 'yr'}</Text>
                      </View>
                      <Text style={s.planYearly}>
                        {isFR ? `soit ${Number(p.price).toFixed(2).replace('.', ',')} €/mois` : `i.e. €${Number(p.price).toFixed(2)}/mo`}
                      </Text>
                    </>
                  ) : (
                    <View style={s.planPriceRow}>
                      <Text style={[s.planPrice, selected === p.tier && s.planPriceActive]}>
                        {(typeof p.price === 'number' ? p.price.toFixed(2).replace('.', ',') : p.price)} €
                      </Text>
                      <Text style={s.planPeriod}>/{isFR ? p.period : 'mo'}</Text>
                    </View>
                  )}
                </TouchableOpacity>
                );
              })}
            </View>

            {/* CTA — v1.12.596 : grisé si l'user est déjà abonné au plan sélectionné */}
            {(() => {
              const isAlreadySubscribed = selected === currentTier
                && (currentPeriod ? billingPeriod === currentPeriod : false);
              const isDisabled = busy || isAlreadySubscribed;
              return (
                <TouchableOpacity
                  style={[s.cta, isDisabled && { opacity: 0.45 }]}
                  onPress={isAlreadySubscribed ? undefined : handleSubscribePress}
                  disabled={isDisabled}
                  activeOpacity={0.85}>
                  <Text style={s.ctaTxt}>
                    {busy
                      ? (isFR ? 'Redirection vers le paiement…' : 'Redirecting to checkout…')
                      : isAlreadySubscribed
                        ? (isFR
                            ? `✓ Déjà abonné — ${currentTierLabel} ${billingPeriod === 'yearly' ? 'annuel' : 'mensuel'}`
                            : `✓ Already subscribed — ${currentTierLabel} ${billingPeriod === 'yearly' ? 'yearly' : 'monthly'}`)
                        : (plan
                            // Apple 3.1.2(c) : le bouton affiche le MONTANT FACTURÉ.
                            // Annuel → total annuel (99,90 €/an), pas le mensuel calculé.
                            ? (billingPeriod === 'yearly' && plan.yearly
                                ? `${isFR ? 'Commencer' : 'Start'} — ${Number(plan.yearly).toFixed(2).replace('.', isFR ? ',' : '.')} €/${isFR ? 'an' : 'yr'}`
                                : `${isFR ? 'Commencer' : 'Start'} — ${(typeof plan.price === 'number' ? plan.price.toFixed(2).replace('.', isFR ? ',' : '.') : plan.price)} €/${isFR ? plan.period : 'mo'}`)
                            : (isFR ? 'Commencer' : 'Start'))}
                  </Text>
                </TouchableOpacity>
              );
            })()}

            <Text style={s.legal}>
              {isFR
                ? "Abonnement auto-renouvelable · Annulable à tout moment · Sans engagement.\nLe paiement est débité lors de la confirmation. L'abonnement se renouvelle automatiquement à la même période et au même tarif, sauf annulation au moins 24 h avant la fin de la période en cours."
                : "Auto-renewable subscription · Cancel anytime · No commitment.\nPayment is charged upon confirmation. The subscription renews automatically at the same price and period unless canceled at least 24 hours before the end of the current period."}
            </Text>

            {/* v1.12.933 — Apple Guideline 3.1.2(c) : liens fonctionnels EULA + Privacy
                obligatoires dans le flux d'achat d'un abonnement auto-renouvelable. */}
            <View style={s.legalLinks}>
              <TouchableOpacity onPress={() => openLegalUrl('terms', getCurrentLang())} hitSlop={{ top:8, bottom:8, left:8, right:8 }}>
                <Text style={s.legalLink}>{isFR ? "Conditions d'utilisation (EULA)" : 'Terms of Use (EULA)'}</Text>
              </TouchableOpacity>
              <Text style={s.legalSep}>·</Text>
              <TouchableOpacity onPress={() => openLegalUrl('privacy', getCurrentLang())} hitSlop={{ top:8, bottom:8, left:8, right:8 }}>
                <Text style={s.legalLink}>{isFR ? 'Politique de confidentialité' : 'Privacy Policy'}</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity onPress={stableOnClose} style={s.skipBtn}>
              <Text style={s.skipTxt}>
                {currentTier === 'free'
                  ? (isFR ? 'Continuer en version gratuite' : 'Continue with the free version')
                  : (isFR ? `Continuer en ${currentTierLabel}` : `Continue with ${currentTierLabel}`)}
              </Text>
            </TouchableOpacity>

          </ScrollView>
        </View>
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  // v1.12.994 — fond assombri sur natif + bordure haute pour détacher la feuille du fond blanc de l'app
  overlay:          { flex:1, justifyContent:'flex-end', backgroundColor: Platform.OS === 'web' ? 'transparent' : 'rgba(0,0,0,0.45)' },
  sheet:            { backgroundColor:'#fff', borderTopLeftRadius:24, borderTopRightRadius:24, maxHeight:'90%', borderTopWidth:1, borderLeftWidth:1, borderRightWidth:1, borderColor:'#E8DCC2', shadowColor:'#000', shadowOffset:{ width:0, height:-6 }, shadowOpacity:0.18, shadowRadius:20, elevation:16 },
  handle:           { width:40, height:4, borderRadius:2, backgroundColor:'#E0E0E0', alignSelf:'center', marginTop:12, marginBottom:4 },

  header:           { alignItems:'center', paddingTop:16, paddingBottom:20, paddingHorizontal:spacing.xl },
  crown:            { fontSize:36, marginBottom:8 },
  // v1.12.937 — logo MyTenue (remplace la couronne) + bouton fermer
  logo:             { width:64, height:64, marginBottom:8 },
  closeBtn:         { position:'absolute', top:12, right:12, width:32, height:32, borderRadius:16, backgroundColor:'#F2F2F2', alignItems:'center', justifyContent:'center', zIndex:10 },
  closeBtnTxt:      { fontSize:16, color:'#666', fontWeight:'600', lineHeight:18 },
  title:            { fontSize:22, fontWeight:'700', color:'#111', letterSpacing:1.5 },
  subtitle:         { fontSize:13, color:'#666', textAlign:'center', marginTop:8, lineHeight:18 },

  features:         { paddingHorizontal:spacing.xl, marginBottom:20 },
  featureRow:       { flexDirection:'row', alignItems:'center', paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#F5F5F5' },
  featureIcon:      { fontSize:20, width:32 },
  featureText:      { flex:1, marginLeft:8 },
  featureLabel:     { fontSize:13, fontWeight:'600', color:'#111' },
  featureSub:       { fontSize:11, color:'#999', marginTop:1 },
  featureCheck:     { fontSize:14, color:'#2E7D32', fontWeight:'700' },

  plans:            { flexDirection:'row', gap:12, paddingHorizontal:spacing.xl, marginBottom:20 },
  planCard:         { flex:1, borderWidth:1.5, borderColor:'#E0E0E0', borderRadius:14, padding:14, alignItems:'center', position:'relative', backgroundColor:'#FAFAFA' },
  planCardActive:   { borderColor:'#111', backgroundColor:'#fff', ...shadow.sm },
  planBadge:        { position:'absolute', top:-10, right:10, backgroundColor:'#111', paddingHorizontal:8, paddingVertical:3, borderRadius:10 },
  planBadgeTxt:     { fontSize:10, color:'#fff', fontWeight:'700' },
  popularDot:       { width:6, height:6, borderRadius:3, backgroundColor:'#2E7D32', position:'absolute', top:8, left:8 },
  planLabel:        { fontSize:11, fontWeight:'500', color:'#999', letterSpacing:1, textTransform:'uppercase', marginBottom:6 },
  planLabelActive:  { color:'#111' },
  planPriceRow:     { flexDirection:'row', alignItems:'baseline', gap:2 },
  planPrice:        { fontSize:26, fontWeight:'300', color:'#999' },
  planPriceActive:  { color:'#111', fontWeight:'600' },
  planPeriod:       { fontSize:11, color:'#999' },
  planYearly:       { fontSize:10, color:'#2E7D32', marginTop:4 },

  cta:              { marginHorizontal:spacing.xl, backgroundColor:'#111', paddingVertical:16, borderRadius:14, alignItems:'center' },
  ctaTxt:           { fontSize:15, fontWeight:'700', color:'#fff', letterSpacing:0.5 },
  legal:            { textAlign:'center', fontSize:10, color:'#BDBDBD', marginTop:10, paddingHorizontal:spacing.xl, lineHeight:14 },
  legalLinks:       { flexDirection:'row', justifyContent:'center', alignItems:'center', flexWrap:'wrap', gap:6, marginTop:8, paddingHorizontal:spacing.xl },
  legalLink:        { fontSize:11, color:'#666', textDecorationLine:'underline' },
  legalSep:         { fontSize:11, color:'#BDBDBD' },
  skipBtn:          { alignItems:'center', paddingVertical:14 },
  skipTxt:          { fontSize:12, color:'#BDBDBD', textDecorationLine:'underline' },
});
